"use client";

import Image from "next/image";
import { L as Link } from "@/components/design";
import { useEffect, useMemo, useRef, useState } from "react";
import { bySlug, catLabel, cutout, plural, rub, type Product } from "@/lib/catalog";
import { useDesign } from "../design";
import { BUNDLE_OFF, cart } from "../cart";
import ModelExhibit from "../ModelExhibit";
import ProductCard from "./ProductCard";
import Stars from "./Stars";
import Arrow from "../Arrow";
import { joke } from "../fun/Joke";

const CITIES = [
  { id: "msk", name: "Москва", courier: [1, 290], pickup: [2, 0] },
  { id: "spb", name: "Санкт-Петербург", courier: [2, 390], pickup: [3, 0] },
  { id: "nn", name: "Нижний Новгород", courier: [3, 390], pickup: [3, 0] },
  { id: "ru", name: "Другой город", courier: [5, 490], pickup: [6, 190] },
] as const;

const SIZES = [
  ["S", "52", "70", "46–48"],
  ["M", "56", "72", "48–50"],
  ["L", "60", "74", "50–52"],
  ["XL", "64", "76", "52–54"],
  ["XXL", "68", "78", "54–56"],
];

const TABS = [
  { id: "about", label: "Описание" },
  { id: "specs", label: "Характеристики" },
  { id: "reviews", label: "Отзывы" },
  { id: "qa", label: "Вопросы" },
  { id: "delivery", label: "Доставка и возврат" },
] as const;
type Tab = (typeof TABS)[number]["id"];

const RECENT = "nii-recent";
const fmtDate = (d: Date) => d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
// timeZone UTC: дата из ISO не должна сдвигаться в часовом поясе браузера (иначе сервер и браузер рисуют разные дни — ошибка гидрации React #418)
const ruDate = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).replace(" г.", "");
// стабильное «сейчас смотрят» для каждого товара, без случайностей между рендерами
const watching = (slug: string) => 2 + ([...slug].reduce((n, c) => n + c.charCodeAt(0), 0) % 6);

export default function ProductView({ slug }: { slug: string }) {
  const base = bySlug(slug) as Product;
  const { design } = useDesign();
  // в «Каталоге» галерея начинается с вырезки на белом
  const p = useMemo(() => (design === "gallery" ? { ...base, images: [{ src: cutout(base), caption: "На белом" }, ...base.images] } : base), [base, design]);
  const [vi, setVi] = useState(0);
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0); // -1 — 3D
  const [tab, setTab] = useState<Tab>("about");
  const [city, setCity] = useState<(typeof CITIES)[number]["id"]>("msk");
  const [today, setToday] = useState<Date | null>(null);
  const [added, setAdded] = useState(false);
  const [onlyPhotos, setOnlyPhotos] = useState(false);
  const [helpful, setHelpful] = useState<Record<number, boolean>>({});
  const [bundle, setBundle] = useState<Record<string, boolean>>(() => Object.fromEntries(p.related.slice(0, 2).map((s) => [s, true])));
  const [recent, setRecent] = useState<string[]>([]);
  const [asked, setAsked] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const lightbox = useRef<HTMLDialogElement>(null);
  const sizeChart = useRef<HTMLDialogElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  const v = p.variants[vi];
  const discount = v.oldPrice ? Math.round((1 - v.price / v.oldPrice) * 100) : 0;
  const c = CITIES.find((x) => x.id === city)!;
  const reviews = useMemo(() => p.reviews.filter((r) => !onlyPhotos || r.photos?.length), [p, onlyPhotos]);
  const related = p.related.map(bySlug).filter((x): x is Product => !!x);
  const bundleItems = related.slice(0, 2);
  const bundleSum = v.price + bundleItems.filter((b) => bundle[b.slug]).reduce((n, b) => n + b.variants[0].price, 0);
  const bundleCount = 1 + bundleItems.filter((b) => bundle[b.slug]).length;
  const bundlePrice =
    bundleCount > 1
      ? Math.round(v.price * (1 - BUNDLE_OFF)) + bundleItems.filter((b) => bundle[b.slug]).reduce((n, b) => n + Math.round(b.variants[0].price * (1 - BUNDLE_OFF)), 0)
      : bundleSum;

  useEffect(() => {
    setToday(new Date());
    try {
      const list: string[] = JSON.parse(localStorage.getItem(RECENT) || "[]").filter((s: string) => s !== slug);
      setRecent(list.slice(0, 4));
      localStorage.setItem(RECENT, JSON.stringify([slug, ...list].slice(0, 8)));
    } catch {
      /* «Вы смотрели» — только удобство */
    }
  }, [slug]);

  useEffect(() => {
    if (photo) lightbox.current?.showModal();
  }, [photo]);

  const addToCart = () => {
    cart.add(p.slug, v.name, qty);
    setAdded(true);
  };
  const days = (n: number) => (today ? fmtDate(new Date(today.getTime() + n * 864e5)) : "…");
  const openTab = (t: Tab) => {
    setTab(t);
    tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const gallery = p.images.length;

  return (
    <>
      <div className="pv">
        {/* ---------- галерея ---------- */}
        <div className="pv-gallery">
          <div className="pv-main">
            {img === -1 && (
              <div className="exhibit pv-3d">
                <ModelExhibit src="/models/pigeon.glb" mobileSrc="/models/pigeon-mobile.glb" />
                <span className="hint label">↻ Вращайте фигурку</span>
              </div>
            )}
            {/* все фото галереи в разметке сразу: первое — с приоритетом, остальные догружаются следом, и листание не ждёт сеть */}
            <button type="button" className="pv-zoom" hidden={img === -1} onClick={() => setPhoto(p.images[img].src)} aria-label="Открыть фото во весь экран">
              {p.images.map((im, i) => (
                <Image
                  key={im.src}
                  src={im.src}
                  alt={i === img ? `${p.name}: ${im.caption}` : ""}
                  width={1200}
                  height={1200}
                  sizes="(max-width: 900px) 100vw, 55vw"
                  className={i === img || (img === -1 && i === 0) ? "is-on" : undefined}
                  {...(i === 0 ? { priority: true } : { loading: "eager" as const, fetchPriority: "low" as const })}
                />
              ))}
            </button>
            <span className="pv-index label">
              {img === -1 ? "3D · модель образца № 0417" : `${String(img + 1).padStart(2, "0")} / ${String(gallery).padStart(2, "0")} — ${p.images[img].caption}`}
            </span>
            {img !== -1 && (
              <div className="pv-arrows">
                <button type="button" aria-label="Предыдущее фото" onClick={() => setImg((img - 1 + gallery) % gallery)}>
                  <Arrow dir="left" />
                </button>
                <button type="button" aria-label="Следующее фото" onClick={() => setImg((img + 1) % gallery)}>
                  <Arrow dir="right" />
                </button>
              </div>
            )}
          </div>
          <div className="pv-thumbs" role="group" aria-label="Фотографии товара">
            {p.images.map((im, i) => (
              <button key={im.src} type="button" aria-pressed={img === i} aria-label={im.caption} onClick={() => setImg(i)}>
                <Image src={im.src} alt="" width={200} height={200} sizes="120px" />
              </button>
            ))}
            {p.has3d && (
              <button type="button" className="pv-thumb3d label" aria-pressed={img === -1} onClick={() => setImg(-1)}>
                3D
                <small>вращать</small>
              </button>
            )}
          </div>
        </div>

        {/* ---------- покупка ---------- */}
        <div className="pv-buy">
          <p className="eyebrow label">
            {catLabel(p.category)} · <span>Арт. {p.sku}</span>
          </p>
          <h1>{p.name}</h1>
          <p className="pv-sub">«{p.subtitle}»</p>
          <button type="button" className="pv-rating" onClick={() => openTab("reviews")}>
            <Stars value={p.rating} size={16} /> <b>{p.rating.toFixed(1).replace(".", ",")}</b>
            <span>
              {p.ratingCount} {plural(p.ratingCount, "отзыв", "отзыва", "отзывов")} · {p.qa.length} {plural(p.qa.length, "вопрос", "вопроса", "вопросов")}
            </span>
          </button>
          <p className="pv-lead">{p.lead}</p>

          {p.variants.length > 1 && (
            <fieldset className="pv-variants">
              <legend className="label">
                {p.variantLabel ?? "Вариант"}: <b>{v.name}</b>
                {p.sizeChart && (
                  <button type="button" className="link" onClick={() => sizeChart.current?.showModal()}>
                    Таблица размеров
                  </button>
                )}
              </legend>
              <div className={`pv-options${p.sizeChart ? " pv-options--sizes" : ""}`}>
                {p.variants.map((o, i) => (
                  <button key={o.name} type="button" aria-pressed={vi === i} onClick={() => { setVi(i); setAdded(false); }}>
                    {o.name}
                    {!p.sizeChart && <small>{rub(o.price)}</small>}
                  </button>
                ))}
              </div>
              {v.note && <p className="pv-note">{v.note}</p>}
            </fieldset>
          )}

          {v.contents && (
            <ol className="pv-contents">
              {v.contents.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
          )}

          <div className="pv-price">
            <b>{rub(v.price)}</b>
            {v.oldPrice && (
              <>
                <s>{rub(v.oldPrice)}</s>
                <span className="badge badge--sale label">−{discount}%</span>
              </>
            )}
          </div>
          <p className="pv-split">
            или 4 платежа по <b>{rub(Math.ceil(v.price / 4))}</b> · +{Math.round(v.price * 0.05)} баллов наблюдателя
          </p>

          <div className="pv-actions">
            <div className="qty" role="group" aria-label="Количество">
              <button type="button" aria-label="Меньше" disabled={qty === 1} onClick={() => setQty(qty - 1)}>
                −
              </button>
              <output aria-live="polite">{qty}</output>
              <button type="button" aria-label="Больше" disabled={qty >= Math.min(99, p.stock)} onClick={() => setQty(qty + 1)}>
                +
              </button>
            </div>
            {added ? (
              <button type="button" className="btn btn-done label" onClick={() => cart.open()}>
                В корзине · перейти →
              </button>
            ) : (
              <button type="button" className="btn btn-p label" onClick={addToCart}>
                В корзину · {rub(v.price * qty)}
              </button>
            )}
          </div>
          <button type="button" className="btn btn-o label pv-oneclick" onClick={() => joke("oneclick")}>
            Купить в 1 клик
          </button>

          <ul className="pv-live label">
            <li>
              <i className="ok" /> {p.stock > 10 ? `В наличии: ${p.stock} шт.` : `Осталось ${p.stock} шт. — поторопитесь, голуби тоже интересуются`}
            </li>
            <li>
              <i /> Сейчас смотрят {watching(slug)} человека и 1 голубь
            </li>
          </ul>

          <div className="pv-delivery">
            <label className="label" htmlFor="city">
              Доставка в
            </label>
            <select id="city" value={city} onChange={(e) => setCity(e.target.value as typeof city)}>
              {CITIES.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
            <dl>
              <dt>Курьером</dt>
              <dd>
                {days(c.courier[0])} · {c.courier[1] ? rub(c.courier[1]) : "бесплатно"}
              </dd>
              <dt>Пункт выдачи «Чердак»</dt>
              <dd>
                {days(c.pickup[0])} · {c.pickup[1] && v.price < 5000 ? rub(c.pickup[1]) : "бесплатно"}
              </dd>
            </dl>
          </div>

          <ul className="pv-assure label">
            <li>Обмен и возврат 14 дней</li>
            <li>Упаковано как улика</li>
            <li>{p.category === "optics" || p.slug.includes("nabor") ? "Гарантия на оптику 2 года" : "Пригодится даже без голубя"}</li>
          </ul>
        </div>
      </div>

      {/* ---------- сравнение комплектаций ---------- */}
      {p.variants.some((x) => x.contents) && (
        <section className="pv-block" aria-labelledby="cmp-title">
          <h2 id="cmp-title">Сравнить комплектации</h2>
          <div className="pv-table">
            <table>
              <thead>
                <tr>
                  <th>Состав</th>
                  {p.variants.map((x, i) => (
                    <th key={x.name} className={vi === i ? "on" : undefined}>
                      {x.name}
                      <small>{rub(x.price)}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from(new Set(p.variants.flatMap((x) => x.contents ?? []))).map((item) => (
                  <tr key={item}>
                    <td>{item}</td>
                    {p.variants.map((x, i) => (
                      <td key={x.name} className={vi === i ? "on" : undefined}>
                        {x.contents?.includes(item) ? "●" : "—"}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td />
                  {p.variants.map((x, i) => (
                    <td key={x.name}>
                      <button type="button" className="link" onClick={() => { setVi(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                        {vi === i ? "Выбрано" : "Выбрать"}
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---------- вкладки ---------- */}
      <section className="pv-tabs" ref={tabsRef}>
        <div className="pv-tablist" role="tablist" aria-label="О товаре">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls={`panel-${t.id}`} onClick={() => setTab(t.id)}>
              {t.label}
              {t.id === "reviews" && <sup>{p.ratingCount}</sup>}
              {t.id === "qa" && <sup>{p.qa.length}</sup>}
            </button>
          ))}
        </div>

        <div role="tabpanel" id="panel-about" aria-labelledby="tab-about" hidden={tab !== "about"} className="pv-panel">
          <h2 className="sr">Описание товара</h2>
          <div className="pv-about">
            <div className="pv-story">
              {p.story.map((s) => (
                <p key={s}>{s}</p>
              ))}
            </div>
            <div className="pv-features">
              {p.features.map((f, i) => (
                <div key={f.title}>
                  <span className="label">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div role="tabpanel" id="panel-specs" aria-labelledby="tab-specs" hidden={tab !== "specs"} className="pv-panel">
          <dl className="pv-specs">
            <div>
              <dt>Артикул</dt>
              <dd>{p.sku}</dd>
            </div>
            {p.specs.map(([k, val]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{val}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div role="tabpanel" id="panel-reviews" aria-labelledby="tab-reviews" hidden={tab !== "reviews"} className="pv-panel">
          <div className="pv-reviews">
            <div className="pv-score">
              <b>{p.rating.toFixed(1).replace(".", ",")}</b>
              <Stars value={p.rating} size={18} />
              <p className="label">
                {p.ratingCount} {plural(p.ratingCount, "оценка", "оценки", "оценок")}
              </p>
              <ul className="pv-dist">
                {p.dist.map((d, i) => (
                  <li key={i}>
                    <span>{5 - i}</span>
                    <i>
                      <span style={{ width: `${d}%` }} />
                    </i>
                    <small>{d}%</small>
                  </li>
                ))}
              </ul>
              <label className="toggle label">
                <input type="checkbox" checked={onlyPhotos} onChange={(e) => setOnlyPhotos(e.target.checked)} /> Только с фото
              </label>
            </div>
            <div className="pv-revlist">
              {reviews.map((r, i) => (
                <article key={r.author + r.date} className="pv-rev">
                  <header>
                    <i className="ava">{r.author[0]}</i>
                    <div>
                      <b>{r.author}</b>
                      <small>
                        {r.role ?? "Покупатель"} · {ruDate(r.date)}
                        {r.variant ? ` · ${r.variant}` : ""}
                      </small>
                    </div>
                    <Stars value={r.rating} />
                  </header>
                  <p>{r.text}</p>
                  {r.pros && (
                    <p className="pv-pc">
                      <b>Достоинства:</b> {r.pros}
                    </p>
                  )}
                  {r.cons && (
                    <p className="pv-pc">
                      <b>Недостатки:</b> {r.cons}
                    </p>
                  )}
                  {r.photos && (
                    <div className="pv-revph">
                      {r.photos.map((ph) => (
                        <button key={ph} type="button" onClick={() => setPhoto(ph)} aria-label="Открыть фото из отзыва">
                          <Image src={ph} alt="" width={160} height={160} sizes="96px" />
                        </button>
                      ))}
                    </div>
                  )}
                  {r.answer && (
                    <div className="pv-answer">
                      <b className="label">Ответ института</b>
                      <p>{r.answer}</p>
                    </div>
                  )}
                  <button type="button" className="pv-helpful label" aria-pressed={!!helpful[i]} onClick={() => setHelpful((h) => ({ ...h, [i]: !h[i] }))}>
                    Полезно · {r.helpful + (helpful[i] ? 1 : 0)}
                  </button>
                </article>
              ))}
              {reviews.length === 0 && <p className="empty">Отзывов с фото пока нет. Вероятно, фотографировали птенца.</p>}
            </div>
          </div>
        </div>

        <div role="tabpanel" id="panel-qa" aria-labelledby="tab-qa" hidden={tab !== "qa"} className="pv-panel">
          <div className="pv-qa">
            {p.qa.map((x) => (
              <div key={x.q} className="pv-q">
                <p>
                  <b>{x.q}</b> <small>— {x.author}</small>
                </p>
                <p className="pv-a">
                  <span className="label">Институт</span> {x.a}
                </p>
              </div>
            ))}
            <form
              className="pv-ask"
              onSubmit={(e) => {
                e.preventDefault();
                setAsked(true);
              }}
            >
              {asked ? (
                <p role="status">Вопрос передан учёному совету. Ответ появится здесь в течение рабочего дня.</p>
              ) : (
                <>
                  <label htmlFor="ask">Задать вопрос о товаре</label>
                  <div>
                    <input id="ask" required placeholder="Например: подойдёт ли для ночных наблюдений?" />
                    <button className="btn btn-p label" type="submit">
                      Спросить
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>

        <div role="tabpanel" id="panel-delivery" aria-labelledby="tab-delivery" hidden={tab !== "delivery"} className="pv-panel">
          <div className="pv-features pv-features--3">
            <div>
              <h3>Курьер института</h3>
              <p>По Москве — на следующий день, по России — 2–7 дней. Бесплатно от 5 000 ₽.</p>
            </div>
            <div>
              <h3>Пункты выдачи «Чердак»</h3>
              <p>1 400 пунктов по стране. Срок хранения — 7 дней, дольше голуби не ждут.</p>
            </div>
            <div>
              <h3>Возврат 14 дней</h3>
              <p>Если товар не пригодился. Если пригодился и вы нашли птенца — возврат не нужен, присылайте фото.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- соберите комплект ---------- */}
      {bundleItems.length > 0 && (
        <section className="pv-block" aria-labelledby="bundle-title">
          <h2 id="bundle-title">Соберите экспедицию — скидка 10%</h2>
          <div className="bundle">
            <div className="bundle__item on">
              <Image src={p.images[0].src} alt="" width={240} height={240} sizes="160px" />
              <span>{p.name}</span>
              <b>{rub(v.price)}</b>
            </div>
            {bundleItems.map((b) => (
              <label key={b.slug} className={`bundle__item${bundle[b.slug] ? " on" : ""}`}>
                <input type="checkbox" checked={!!bundle[b.slug]} onChange={(e) => setBundle((s) => ({ ...s, [b.slug]: e.target.checked }))} />
                <Image src={b.images[0].src} alt="" width={240} height={240} sizes="160px" />
                <span>{b.name}</span>
                <b>{rub(b.variants[0].price)}</b>
              </label>
            ))}
            <div className="bundle__sum">
              {bundleCount > 1 && <s>{rub(bundleSum)}</s>}
              <b>{rub(bundlePrice)}</b>
              <button
                type="button"
                className="btn btn-p label"
                onClick={() => {
                  const many = bundleCount > 1;
                  cart.add(p.slug, v.name, 1, many);
                  bundleItems.filter((b) => bundle[b.slug]).forEach((b) => cart.add(b.slug, b.variants[0].name, 1, many));
                  cart.open();
                }}
              >
                Добавить {bundleCount} {plural(bundleCount, "товар", "товара", "товаров")}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="pv-block" aria-labelledby="rel-title">
        <h2 id="rel-title">С этим берут</h2>
        <div className="grid4">
          {related.map((r) => (
            <ProductCard p={r} key={r.slug} />
          ))}
        </div>
      </section>

      {recent.length > 0 && (
        <section className="pv-block" aria-labelledby="recent-title">
          <h2 id="recent-title">Вы смотрели</h2>
          <div className="recent">
            {recent
              .map(bySlug)
              .filter((x): x is Product => !!x)
              .map((r) => (
                <Link key={r.slug} href={`/catalog/${r.slug}`} className="recent__item">
                  <Image src={r.images[0].src} alt="" width={160} height={160} sizes="96px" />
                  <span>{r.name}</span>
                </Link>
              ))}
          </div>
        </section>
      )}

      {/* ---------- панель покупки на телефоне ---------- */}
      <div className="pv-sticky">
        <div>
          <b>{rub(v.price * qty)}</b>
          <small>{p.variants.length > 1 ? v.name : p.name}</small>
        </div>
        <button type="button" className={`btn label ${added ? "btn-done" : "btn-p"}`} onClick={added ? () => cart.open() : addToCart}>
          {added ? "В корзине →" : "В корзину"}
        </button>
      </div>

      {/* ---------- диалоги ---------- */}
      <dialog className="lightbox" ref={lightbox} onClose={() => setPhoto(null)} onClick={(e) => e.target === e.currentTarget && lightbox.current?.close()}>
        <button type="button" className="x" aria-label="Закрыть фото" onClick={() => lightbox.current?.close()}>
          ×
        </button>
        {photo && <Image src={photo} alt={p.name} width={1400} height={1400} sizes="90vw" />}
      </dialog>


      {p.sizeChart && (
        <dialog className="modal" ref={sizeChart} aria-labelledby="size-title" onClick={(e) => e.target === e.currentTarget && sizeChart.current?.close()}>
          <button type="button" className="x" aria-label="Закрыть" onClick={() => sizeChart.current?.close()}>
            ×
          </button>
          <h2 id="size-title">Таблица размеров</h2>
          <table className="sizes">
            <thead>
              <tr>
                <th>Размер</th>
                <th>Ширина, см</th>
                <th>Длина, см</th>
                <th>Российский</th>
              </tr>
            </thead>
            <tbody>
              {SIZES.map((r) => (
                <tr key={r[0]} className={r[0] === v.name ? "on" : undefined}>
                  {r.map((x) => (
                    <td key={x}>{x}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pv-note">Крой свободный. Если сомневаетесь, берите меньший размер. Голубям размер не важен.</p>
        </dialog>
      )}
    </>
  );
}

